import { useRef } from "react";
import type { ChangeEvent } from "react";

import type { SmileSimulationController } from "../hooks/useSmileSimulationController";
import { getSmileSimulationCopy } from "../model/i18n";
import type { SmileSimulationLanguage } from "../model/types";

type SmileSimulationDownloadPreviewModalProps = {
  download: SmileSimulationController["download"];
  language: SmileSimulationLanguage;
  onClose: SmileSimulationController["actions"]["closeDownloadPreview"];
  onConfirm: SmileSimulationController["actions"]["confirmDownloadPreview"];
  onToggleClinicWatermark: SmileSimulationController["actions"]["toggleDownloadPreviewClinicWatermark"];
  onToggleServiceWatermark: SmileSimulationController["actions"]["toggleDownloadPreviewServiceWatermark"];
  onUploadClinicWatermark: SmileSimulationController["actions"]["uploadDownloadPreviewClinicWatermark"];
};

export function SmileSimulationDownloadPreviewModal({
  download,
  language,
  onClose,
  onConfirm,
  onToggleClinicWatermark,
  onToggleServiceWatermark,
  onUploadClinicWatermark,
}: SmileSimulationDownloadPreviewModalProps) {
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const copy = getSmileSimulationCopy(language);

  const handleUploadChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0];
    event.target.value = "";

    if (!nextFile) {
      return;
    }

    await onUploadClinicWatermark(nextFile);
  };

  if (!download.isPreviewOpen) {
    return null;
  }

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-[#13071f]/55 px-4 py-6 backdrop-blur-sm">
      <div className="flex max-h-[calc(100svh-3rem)] w-full max-w-[32rem] flex-col overflow-hidden rounded-[2rem] border border-white/70 bg-white/95 shadow-[0_30px_80px_rgba(19,7,31,0.25)]">
        <div className="border-b border-[#eadcf8] px-5 py-4">
          <h2 className="text-lg font-semibold text-[#1f1230]">
            {copy.download.title}
          </h2>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
          <label className="flex items-center justify-between gap-4 rounded-[1.25rem] border border-[#eadcf8] bg-[#faf7ff] px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-[#1f1230]">
                {copy.download.serviceWatermark}
              </p>
            </div>
            <input
              type="checkbox"
              checked={download.includeServiceWatermark}
              onChange={(event) =>
                onToggleServiceWatermark(event.target.checked)
              }
              className="h-5 w-5 accent-[#8F14E5]"
            />
          </label>

          <label className="flex items-center justify-between gap-4 rounded-[1.25rem] border border-[#eadcf8] bg-[#faf7ff] px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-[#1f1230]">
                {copy.download.clinicWatermark}
              </p>
            </div>
            <input
              type="checkbox"
              checked={download.includeClinicWatermark}
              onChange={(event) =>
                onToggleClinicWatermark(event.target.checked)
              }
              disabled={!download.hasClinicWatermark}
              className="h-5 w-5 accent-[#8F14E5]"
            />
          </label>

          <div className="flex min-h-0 flex-1 rounded-[1.75rem] bg-[#13071f] p-3">
            <div className="relative flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-[1.25rem] bg-[#13071f]">
              {download.previewImage ? (
                <img
                  src={download.previewImage}
                  alt="Download preview"
                  className="block h-full w-full object-contain"
                />
              ) : null}

              {download.isPreparingPreview ? (
                <div className="bg-[#13071f]/72 absolute inset-0 flex items-center justify-center px-6 text-center text-sm font-medium text-white">
                  {copy.download.preparing}
                </div>
              ) : null}
            </div>
          </div>

          {download.previewErrorMessage ? (
            <p className="text-sm text-[#c2410c]">
              {download.previewErrorMessage}
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[#eadcf8] px-5 py-4">
          <div>
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleUploadChange}
            />
            <button
              type="button"
              onClick={() => uploadInputRef.current?.click()}
              disabled={download.isUploadingClinicWatermark}
              className="rounded-full border border-[#8F14E5]/20 bg-white px-4 py-2 text-sm font-semibold text-[#8F14E5] transition hover:bg-[#faf7ff] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {download.isUploadingClinicWatermark
                ? copy.download.uploading
                : copy.download.uploadWatermark}
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-white/70 bg-white px-4 py-2 text-sm font-semibold text-[#6b577d] transition hover:bg-[#faf7ff]"
            >
              {copy.download.cancel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={
                download.isDownloadingImage || download.isPreparingPreview
              }
              className="rounded-full bg-[#8F14E5] px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(143,20,229,0.22)] transition hover:bg-[#7d10ca] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {download.isDownloadingImage
                ? copy.download.downloading
                : copy.download.download}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
